import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, timer } from 'rxjs';
import { finalize, switchMap, take, takeUntil, takeWhile } from 'rxjs/operators';
import { AppConfigService } from 'src/app/appConfig.service';
import { EntitlementsService } from 'src/app/core/service/entitlements.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { BillingPlans, BillingPrice, BillingService, BillingTier, ChangePlanPreview } from '../billing.service';

declare const Stripe: any;

export interface UpgradeModalOptions {
  /** What asked for the upgrade, e.g. 'inbox-automator'. Recorded on the Stripe session. */
  reason?: string;
  /** A feature code to highlight: the cheapest tier that includes it is recommended. */
  requiredFeature?: string;
  /** How to name that feature in the heading, e.g. 'Inbox Automator'. */
  featureName?: string;
}

type Step = 'loading' | 'plans' | 'managed' | 'checkout' | 'change' | 'confirming' | 'done' | 'error';

/**
 * SD-1229: choose a plan and pay for it without leaving the page.
 *
 * Opened through UpgradeModalService from anywhere -- the Inbox Automator prompt, a
 * "not on your plan" response, the billing page. An organisation with no paid subscription
 * pays through Stripe Embedded Checkout, mounted inside this modal: the card form is Stripe's,
 * so card details never touch Synced, and Stripe handles 3-D Secure, tax and promotion codes.
 * An organisation that already pays changes plan in place, with Stripe's own proration shown
 * first.
 *
 * The modal never decides the plan changed. It waits for the API -- fed by Stripe's webhook,
 * or by the status poll reading the same subscription -- to report the new plan, then
 * refreshes entitlements so the feature that prompted the upgrade unlocks at once.
 */
@Component({
  selector: 'app-upgrade-modal',
  templateUrl: './upgrade-modal.component.html',
  styleUrls: ['./upgrade-modal.component.scss']
})
export class UpgradeModalComponent implements OnInit, OnDestroy {
  @Input() options: UpgradeModalOptions = {};

  @ViewChild('checkoutHost') checkoutHost: ElementRef<HTMLElement>;

  step: Step = 'loading';
  plans: BillingPlans | null = null;
  tiers: BillingTier[] = [];
  recommendedCode: string | null = null;
  errorMessage = '';

  selectedTier: BillingTier | null = null;
  selectedPrice: BillingPrice | null = null;
  preview: ChangePlanPreview | null = null;
  busy = false;
  slowConfirm = false;
  /** Paid, but the new plan had not come through when the modal stopped waiting. */
  pending = false;

  private organizationId: string;
  private embeddedCheckout: any = null;
  private sessionId: string | null = null;
  private startingPlanCode: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private billing: BillingService,
    private entitlements: EntitlementsService,
    private organizationService: OrganizationService,
    private appConfig: AppConfigService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.organizationId = this.organizationService.selectedOrganization;
    this.load();
  }

  ngOnDestroy(): void {
    this.destroyCheckout();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get title(): string {
    if (this.step === 'done') { return 'You’re all set'; }
    if (this.step === 'managed') { return this.options?.featureName ? 'Add ' + this.options.featureName + ' to your plan' : 'Your plan'; }
    if (this.step === 'change' && this.selectedTier) { return 'Change to ' + this.selectedTier.name; }
    if (this.step === 'checkout' && this.selectedTier) { return 'Subscribe to ' + this.selectedTier.name; }
    if (this.options?.featureName) { return 'Upgrade to use ' + this.options.featureName; }
    return 'Choose a plan';
  }

  /** Room for the plan cards and Stripe's form; the short steps size to their content. */
  get roomy(): boolean {
    return this.step === 'loading' || this.step === 'plans' || this.step === 'checkout';
  }

  /**
   * The current plan as a customer should read it. 'legacy' is an internal tier (existing
   * organisations, and products not yet mapped); its admin name "Legacy / unmapped" is not
   * something to show a customer.
   */
  get currentPlanLabel(): string {
    const plan = this.plans?.currentPlan;
    if (!plan) { return ''; }
    return plan.code === 'legacy' ? 'your current plan' : plan.name;
  }

  readonly supportEmail = 'support@syncedhq.com';

  get supportMailto(): string {
    const subject = this.options?.featureName ? 'Add ' + this.options.featureName + ' to our plan' : 'Change our plan';
    return 'mailto:' + this.supportEmail + '?subject=' + encodeURIComponent(subject);
  }

  get canManageBilling(): boolean {
    return !!this.plans?.canManageBilling;
  }

  /** Paying for an upgrade: new subscribers go to Checkout, existing ones change in place. */
  get isExistingSubscriber(): boolean {
    return !!this.plans?.hasActiveSubscription;
  }

  load(): void {
    this.step = 'loading';
    this.billing.plans(this.organizationId).pipe(take(1), takeUntil(this.destroy$)).subscribe(plans => {
      this.plans = plans;
      this.startingPlanCode = plans?.currentPlan?.code || null;
      this.tiers = (plans?.tiers || []).filter(t => t.prices && t.prices.length > 0);
      this.recommendedCode = this.pickRecommended();
      // SD-1229: a plan Synced manages and invoices by hand is changed by talking to Synced,
      // not through checkout -- every upgrade prompt lands here, so this is the one place.
      this.step = plans?.managed ? 'managed' : 'plans';
    }, err => this.fail(err, 'We couldn’t load the plans. Please try again.'));
  }

  /** The cheapest tier above the current one that includes the feature asked for. */
  private pickRecommended(): string | null {
    const current = this.plans?.currentPlan?.rank ?? 0;
    const candidates = this.tiers
      .filter(t => !t.isCurrent && t.rank >= current)
      .filter(t => !this.options?.requiredFeature || t.features.some(f => f.code === this.options.requiredFeature));
    return candidates.length ? candidates.sort((a, b) => a.rank - b.rank)[0].code : null;
  }

  includesRequired(tier: BillingTier): boolean {
    return !!this.options?.requiredFeature && tier.features.some(f => f.code === this.options.requiredFeature);
  }

  isCurrentPrice(price: BillingPrice): boolean {
    return !!this.plans?.currentPriceId && this.plans.currentPriceId === price.priceId;
  }

  choose(tier: BillingTier, price: BillingPrice): void {
    if (!this.canManageBilling || this.isCurrentPrice(price)) { return; }
    this.selectedTier = tier;
    this.selectedPrice = price;
    this.errorMessage = '';

    if (this.isExistingSubscriber) {
      this.startChangePlan();
    } else {
      this.startCheckout();
    }
  }

  back(): void {
    this.destroyCheckout();
    this.preview = null;
    this.selectedPrice = null;
    this.selectedTier = null;
    this.errorMessage = '';
    this.step = 'plans';
  }

  // ── New subscription: Stripe Embedded Checkout ────────────────────────────

  private startCheckout(): void {
    const publishableKey = this.appConfig.stripePublishableKey;
    if (!publishableKey) {
      this.fail(null, 'Payments are not set up in this environment yet.');
      return;
    }
    if (typeof Stripe === 'undefined') {
      this.fail(null, 'We couldn’t load Stripe. Check that your browser isn’t blocking js.stripe.com.');
      return;
    }

    this.step = 'checkout';
    this.busy = true;
    // The host element only exists once the checkout step has rendered.
    this.cdr.detectChanges();

    const stripe = Stripe(publishableKey);
    const fetchClientSecret = () =>
      this.billing.createCheckoutSession(this.organizationId, this.selectedPrice.priceId, this.options?.reason)
        .toPromise()
        .then(session => {
          this.sessionId = session.sessionId;
          return session.clientSecret;
        });

    stripe.initEmbeddedCheckout({
      fetchClientSecret,
      // Stripe calls this outside Angular's zone.
      onComplete: () => this.zone.run(() => this.confirmCheckout())
    }).then((checkout: any) => {
      this.embeddedCheckout = checkout;
      if (this.step !== 'checkout' || !this.checkoutHost) {
        this.destroyCheckout();
        return;
      }
      checkout.mount(this.checkoutHost.nativeElement);
      this.zone.run(() => this.busy = false);
    }).catch((err: any) => this.zone.run(() => {
      const message = err?.error?.error || err?.message;
      this.fail(err, message || 'We couldn’t start checkout. Please try again.');
    }));
  }

  /** After payment: wait for the API to report the new plan, then unlock it everywhere. */
  private confirmCheckout(): void {
    this.destroyCheckout();
    this.step = 'confirming';
    this.slowConfirm = false;

    let attempts = 0;
    timer(0, 1500).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.billing.checkoutStatus(this.sessionId, this.organizationId)),
      takeWhile(status => {
        attempts++;
        if (attempts === 8) { this.slowConfirm = true; }
        const planChanged = status.status === 'complete' && status.planCode !== this.startingPlanCode;
        return !planChanged && attempts < 20;
      }, true)
    ).subscribe(status => {
      if (status.status === 'complete' && status.planCode !== this.startingPlanCode) {
        this.finish();
      } else if (attempts >= 20) {
        // Paid, but the plan has not come through yet: the webhook is behind. Say so plainly --
        // it will apply without the customer doing anything.
        this.finish(true);
      }
    }, () => this.finish(true));
  }

  // ── Existing subscriber: change of plan ────────────────────────────────────

  private startChangePlan(): void {
    this.step = 'change';
    this.preview = null;
    this.busy = true;
    this.billing.previewChangePlan(this.organizationId, this.selectedPrice.priceId)
      .pipe(take(1), takeUntil(this.destroy$), finalize(() => this.busy = false))
      .subscribe(preview => this.preview = preview,
        err => this.fail(err, 'We couldn’t price that change. Please try again.'));
  }

  confirmChangePlan(): void {
    this.busy = true;
    this.errorMessage = '';
    this.billing.changePlan(this.organizationId, this.selectedPrice.priceId)
      .pipe(take(1), takeUntil(this.destroy$), finalize(() => this.busy = false))
      .subscribe(() => this.finish(), err => {
        // A declined proration leaves the plan as it was; show Stripe's reason in place.
        this.errorMessage = err?.error?.error || 'The change could not be paid for.';
      });
  }

  // ── Outcome ────────────────────────────────────────────────────────────────

  private finish(pending = false): void {
    this.pending = pending;
    this.entitlements.refresh();
    this.step = 'done';
  }

  /** Resolves true only when a plan was bought or changed. */
  close(): void {
    this.activeModal.close(this.step === 'done');
  }

  private destroyCheckout(): void {
    if (this.embeddedCheckout) {
      try { this.embeddedCheckout.destroy(); } catch { /* already gone */ }
      this.embeddedCheckout = null;
    }
  }

  private fail(err: any, message: string): void {
    this.destroyCheckout();
    this.errorMessage = err?.error?.error && typeof err.error.error === 'string' ? err.error.error : message;
    this.step = 'error';
    this.busy = false;
  }

  // ── Display ────────────────────────────────────────────────────────────────

  priceLabel(price: BillingPrice): string {
    const amount = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (price.currency || 'usd').toUpperCase(),
      maximumFractionDigits: price.amount % 1 === 0 ? 0 : 2
    }).format(price.amount);
    return amount + (price.interval ? ' / ' + price.interval : '');
  }

  money(amount: number, currency: string): string {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount || 0);
  }

  trackTier = (_: number, tier: BillingTier) => tier.code;
  trackPrice = (_: number, price: BillingPrice) => price.priceId;
}
